import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { toast } from "sonner";

export default function BusinessSubscribe() {
  useSeo({ title: "Choose your plan — BookFreely", path: "/business/subscribe", noindex: true });
  const { user, authHeader } = useAuth();
  const [loading, setLoading] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [config, setConfig] = useState({
    trial_days: 0,
    monthly_price: 19.99,
    yearly_price: 199.99,
    real_subscription_active: false,
  });
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/subscription/config`).then((r) => setConfig((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);

  const subscribe = async (tier) => {
    if (!user) { nav("/login?next=/business/subscribe"); return; }
    if (user.role !== "business") { toast.error("Only businesses subscribe."); return; }
    setLoading(tier);
    setCheckoutUrl(null);
    try {
      const r = await axios.post(
        `${API}/subscription/checkout`,
        { tier, origin_url: window.location.origin },
        { headers: authHeader() }
      );
      const d = r?.data || {};
      const action = d.action;
      // Already subscribed / paused / payment due → send to billing management.
      if (d.blocked && action === "manage_billing") {
        toast.info(d.message || "You already have an active membership.");
        if (d.url) { setCheckoutUrl(d.url); window.location.href = d.url; }
        else { nav("/business/billing"); }
        setLoading(null);
        return;
      }
      // An incomplete checkout is still payable → resume it.
      if (action === "resume" && d.url) {
        toast.info(d.message || "Resuming your checkout…");
        setCheckoutUrl(d.url);
        window.location.href = d.url;
        setLoading(null);
        return;
      }
      // Plan switched in place (no new subscription created).
      if (action === "plan_changed") {
        toast.success(d.message || "Your plan was updated.");
        setLoading(null);
        return;
      }
      // Plan change needs confirmation of the exact charges → do it in billing.
      if (action === "plan_change_preview") {
        toast.info("Review the charges for your plan change in Billing.");
        nav("/business/billing");
        setLoading(null);
        return;
      }
      const url = d.url;
      // Defence in depth: only ever redirect to a real Stripe-hosted HTTPS URL.
      // If the backend ever regresses (returns null/undefined/non-Stripe), we surface
      // a clear message instead of navigating to about:blank.
      if (!url || typeof url !== "string" || !/^https:\/\/([a-z0-9-]+\.)?stripe\.com\//i.test(url)) {
        toast.error("Checkout couldn't start — please try again in a moment.");
        setLoading(null);
        return;
      }
      // Show a visible fallback link too, in case the browser blocks the auto-redirect
      // (rare — Safari ITP, extensions, popup blockers). The user can click through
      // manually without losing the session URL.
      setCheckoutUrl(url);
      window.location.href = url;
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 503 && det?.error === "subscription_verification_unavailable") {
        toast.error(det.message || "We couldn't verify your membership just now. Please try again in a moment.");
      } else {
        toast.error((typeof det === "string" ? det : det?.message) || "Could not start checkout");
      }
      setLoading(null);
    }
  };

  // 7-day free trial applies ONLY to the Monthly plan, once per business.
  // Yearly (including the yearly first-year promo) never carries a trial —
  // that's the pricing decision confirmed Feb 23 2026.
  const monthlyTrialAvailable = (config.trial_days || 0) > 0 && !user?.has_used_monthly_trial;
  const monthlyTrialUsed = (config.trial_days || 0) > 0 && user?.has_used_monthly_trial;
  const yearlyPrice = config.yearly_price ?? 199.99;

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Business subscription</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "var(--gf-text)" }}>Choose your plan</h1>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--gf-text-soft)" }}>
          A simple business subscription with no per-booking commission on your own and returning customers. New marketplace customers carry a one-time 10% acquisition fee, capped at $30, on their first booking only. Securely powered by Stripe.
        </p>

        {checkoutUrl && (
          <div data-testid="checkout-fallback-banner" className="mt-6 rounded-2xl border p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3"
               style={{ borderColor: "var(--gf-accent)", background: "var(--gf-surface)" }}>
            <div className="flex-1 text-sm" style={{ color: "var(--gf-text-soft)" }}>
              Redirecting you to secure checkout… If nothing happens in a few seconds, your browser may have blocked the redirect.
            </div>
            <a data-testid="checkout-fallback-link" href={checkoutUrl}
               className="rounded-full px-5 py-2 text-sm font-medium whitespace-nowrap"
               style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Continue to checkout →
            </a>
          </div>
        )}

        {monthlyTrialAvailable && (
          <div data-testid="trial-banner" className="mt-6 rounded-2xl border p-4 flex items-start gap-3" style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)" }}>
            <span className="inline-block w-2 h-2 rounded-full mt-2" style={{ background: "var(--gf-accent)" }} />
            <div className="text-sm" style={{ color: "var(--gf-text-soft)" }}>
              <b style={{ color: "var(--gf-text)" }}>Try Monthly free for {config.trial_days} days.</b>{" "}
              A valid card is collected up-front via Stripe. You won&rsquo;t be charged during the trial, and Monthly billing ($
              {(config.monthly_price ?? 19.99).toFixed(2)}/mo) begins automatically when the trial ends unless you cancel first.
              The 7-day free trial applies to the Monthly plan only — Yearly plans start billing right away.
            </div>
          </div>
        )}
        {monthlyTrialUsed && (
          <div data-testid="trial-used-banner" className="mt-6 rounded-2xl border p-4 text-sm" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-soft)" }}>
            You&rsquo;ve previously used your {config.trial_days}-day free trial — subscribing now bills immediately.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {/* Monthly plan */}
          <div data-testid="tier-card-monthly" className="rounded-2xl border p-7 flex flex-col"
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Monthly</div>
              {monthlyTrialAvailable && (
                <span className="text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5" style={{ background: "var(--gf-surface-2)", color: "var(--gf-accent)" }} data-testid="trial-pill-monthly">
                  {config.trial_days}-day free trial
                </span>
              )}
            </div>
            {monthlyTrialAvailable ? (
              <>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl" style={{ color: "var(--gf-text)" }} data-testid="monthly-today-price">$0</span>
                  <span style={{ color: "var(--gf-text-muted)" }}>due today</span>
                </div>
                {(() => {
                  const trialEnd = new Date(Date.now() + (config.trial_days || 7) * 86400000);
                  const fmt = trialEnd.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
                  return (
                    <div className="mt-3 text-sm space-y-1.5" data-testid="monthly-trial-explainer" style={{ color: "var(--gf-text-soft)" }}>
                      <div>· <b>{config.trial_days}-day free trial</b></div>
                      <div>· <b>$0 due today</b></div>
                      <div>· Then <b>${(config.monthly_price ?? 19.99).toFixed(2)}/month</b> starting on <b data-testid="monthly-trial-end-date">{fmt}</b></div>
                      <div style={{ color: "var(--gf-text-muted)" }}>· Cancel anytime before <b>{fmt}</b> to avoid being charged</div>
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl" style={{ color: "var(--gf-text)" }}>${(config.monthly_price ?? 19.99).toFixed(2)}</span>
                  <span style={{ color: "var(--gf-text-muted)" }}>/mo</span>
                </div>
                <div className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>Cancel anytime</div>
              </>
            )}
            <ul className="text-sm mt-5 space-y-2" style={{ color: "var(--gf-text-soft)" }}>
              <li>· Unlimited service listings</li>
              <li>· Custom deposit per service</li>
              <li>· Built-in calendar &amp; reminders</li>
              <li>· Automatic payouts to your bank</li>
            </ul>
            <button
              data-testid="subscribe-monthly-btn"
              disabled={loading === "monthly"}
              onClick={() => subscribe("monthly")}
              className="mt-6 w-full rounded-full py-3 font-medium transition-colors disabled:opacity-50"
              style={{ background: "transparent", color: "var(--gf-text)", border: "1px solid var(--gf-border-strong)" }}
            >
              {loading === "monthly" ? "Redirecting to Stripe…" : monthlyTrialAvailable ? `Start ${config.trial_days}-day free trial` : "Continue to Stripe"}
            </button>
            {monthlyTrialAvailable && (
              <p className="text-[11px] mt-2 text-center" style={{ color: "var(--gf-text-muted)" }}>
                Card required up-front. No charge during the trial.
              </p>
            )}
          </div>

          {/* Yearly plan — NO trial. Flat $199.99/yr. */}
          <div data-testid="tier-card-yearly" className="rounded-2xl border p-7 flex flex-col relative"
               style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)" }}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Yearly</div>
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-display text-4xl" style={{ color: "var(--gf-text)" }} data-testid="yearly-price">${yearlyPrice.toFixed(2)}</span>
              <span style={{ color: "var(--gf-text-muted)" }}>/yr</span>
            </div>
            <div className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }} data-testid="yearly-renewal-note">
              Standard yearly subscription. Charged on the same day each year.
            </div>
            <ul className="text-sm mt-5 space-y-2" style={{ color: "var(--gf-text-soft)" }}>
              <li>· Everything in Monthly</li>
              <li>· Save ${(((config.monthly_price ?? 19.99) * 12) - yearlyPrice).toFixed(2)} vs. paying monthly</li>
              <li>· One annual charge, no surprises</li>
              <li>· Priority support response</li>
            </ul>
            <button
              data-testid="subscribe-yearly-btn"
              disabled={loading === "yearly"}
              onClick={() => subscribe("yearly")}
              className="mt-6 w-full rounded-full py-3 font-medium transition-colors disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {loading === "yearly" ? "Redirecting to Stripe…" : "Continue to Stripe"}
            </button>
            <p className="text-[11px] mt-2 text-center" style={{ color: "var(--gf-text-muted)" }}>
              Billed today. No free trial on Yearly.
            </p>
          </div>
        </div>

        <div className="mt-12 text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="subscribe-stripe-note">
          Payments are processed securely by Stripe. Choosing a plan redirects you to Stripe's hosted checkout.
          The first-year yearly promotion is available once per business — Stripe customer records are checked to prevent repeat redemption.
        </div>

        <div className="mt-6 rounded-2xl border p-4 text-xs leading-relaxed" data-testid="dispute-responsibility-disclosure"
             style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-soft)" }}>
          <b>Chargeback responsibility.</b> If a customer's bank disputes a payment and the dispute is lost,
          you (the Business) bear the net service amount you were paid for that booking — BookFreely reverses only
          the net service proceeds actually transferred to you (never more than you received, after any refunds).
          BookFreely absorbs its own Booking &amp; Processing Fee, any retained marketplace acquisition fee, and the
          Stripe dispute fee. A reversal may create a negative balance on your Stripe account, which Stripe recovers
          from your future incoming funds; BookFreely does not separately charge your card or bank. A dispute being
          opened does not by itself suspend your account. By continuing you accept these terms before enabling paid
          bookings. See the <a href="/terms" className="underline">Terms</a> (Section 11) for full detail.
        </div>
      </div>
      <Footer />
    </div>
  );
}
